// src/services/ScheduledValidationService.js
// Manages scheduled model validation: configure frequency, enable/disable,
// store run history, and trigger due validations on demand.

import storage from './StorageAdapter';
import auditService from './AuditService';
import entityService from './EntityService';

const SCHEDULES_KEY = 'scheduled_validation_schedules';
const HISTORY_KEY = 'scheduled_validation_history';
const MAX_HISTORY = 50;

class ScheduledValidationService {
  constructor() {
    this.listeners = [];
  }

  _entityKey(base) {
    const eid = entityService.getActiveEntityId();
    return eid ? `${base}_${eid}` : base;
  }

  // ── Schedules ────────────────────────────────────────────────────────────

  /**
   * Returns all schedule configs, keyed by modelKey.
   * modelKey format: 'georisk' | 'customerrisk_<modelName>'
   */
  getSchedules() {
    return storage.getJSON(this._entityKey(SCHEDULES_KEY), {});
  }

  getSchedule(modelKey) {
    const all = this.getSchedules();
    return all[modelKey] || {
      enabled: false,
      frequency: 'weekly',
      weekday: 1, // Monday (0=Sun, 1=Mon, …, 6=Sat)
      lastRun: null,
      nextRun: null,
    };
  }

  /**
   * Upsert a schedule for a model.
   * @param {string} modelKey
   * @param {object} config - { enabled, frequency, datasetId, datasetLabel, modelName }
   */
  async setSchedule(modelKey, config) {
    const all = this.getSchedules();
    const existing = all[modelKey] || {};
    const updated = {
      ...existing,
      ...config,
      updatedAt: new Date().toISOString(),
    };

    // Compute nextRun when enabling, changing frequency, or changing weekday
    if (config.enabled && (
      !existing.enabled ||
      config.frequency !== existing.frequency ||
      (config.weekday !== undefined && config.weekday !== existing.weekday)
    )) {
      updated.nextRun = this._computeNextRun(updated.frequency, existing.lastRun, updated.weekday);
    }
    if (!config.enabled) {
      updated.nextRun = null;
    }

    all[modelKey] = updated;
    await storage.setJSON(this._entityKey(SCHEDULES_KEY), all);
    this._notify();
  }

  // ── History ──────────────────────────────────────────────────────────────

  getHistory() {
    return storage.getJSON(this._entityKey(HISTORY_KEY), []);
  }

  getHistoryForModel(modelKey) {
    return this.getHistory().filter(r => r.modelKey === modelKey);
  }

  getLatestRun(modelKey) {
    const runs = this.getHistoryForModel(modelKey);
    return runs[0] || null;
  }

  /**
   * Record a completed validation run in history.
   */
  async _recordRun(modelKey, result, triggeredBy = 'manual') {
    const entry = {
      id: `svr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      modelKey,
      triggeredBy,
      timestamp: new Date().toISOString(),
      accuracy: result.accuracy ?? null,
      total: result.total ?? null,
      correct: result.correct ?? null,
      datasetId: result.datasetId || null,
      datasetLabel: result.datasetLabel || null,
      modelName: result.modelName || null,
      status: 'success',
    };

    const history = this.getHistory();
    history.unshift(entry);
    if (history.length > MAX_HISTORY) history.splice(MAX_HISTORY);
    await storage.setJSON(this._entityKey(HISTORY_KEY), history);

    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('validationCompleted', { modelKey, accuracy: entry.accuracy, triggeredBy, status: 'success' });
    } catch (_e) { /* best-effort */ }

    return entry;
  }

  async _recordFailedRun(modelKey, error, triggeredBy = 'manual') {
    const entry = {
      id: `svr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      modelKey,
      triggeredBy,
      timestamp: new Date().toISOString(),
      accuracy: null,
      total: null,
      correct: null,
      status: 'error',
      error: error?.message || String(error),
    };

    const history = this.getHistory();
    history.unshift(entry);
    if (history.length > MAX_HISTORY) history.splice(MAX_HISTORY);
    await storage.setJSON(this._entityKey(HISTORY_KEY), history);

    return entry;
  }

  // ── Run ──────────────────────────────────────────────────────────────────

  /**
   * Manually trigger a validation run for a model.
   * Requires dataValidationService to be passed in to avoid circular imports.
   *
   * @param {string} modelKey
   * @param {object} dataValidationService
   * @returns {object} history entry
   */
  async runValidation(modelKey, dataValidationService) {
    const schedule = this.getSchedule(modelKey);

    let result;
    try {
      if (modelKey === 'georisk') {
        if (!schedule.datasetId) throw new Error('No dataset configured for GeoRisk validation');
        result = await dataValidationService.runGeoValidation(schedule.datasetId);
      } else if (modelKey.startsWith('customerrisk_')) {
        const modelName = modelKey.replace('customerrisk_', '');
        if (!schedule.datasetId) throw new Error('No dataset configured for CustomerRisk validation');
        result = await dataValidationService.runCustomerValidation(schedule.datasetId, modelName);
      } else {
        throw new Error(`Unknown modelKey: ${modelKey}`);
      }
    } catch (err) {
      const entry = await this._recordFailedRun(modelKey, err, 'manual');
      await this._updateLastRun(modelKey);
      auditService.log('UPDATE', 'Admin', {
        entity: 'ScheduledValidation',
        name: modelKey,
        result: 'error',
        error: err.message,
      });
      this._notify();
      return entry;
    }

    const entry = await this._recordRun(modelKey, result, 'manual');
    await this._updateLastRun(modelKey);

    auditService.log('UPDATE', 'Admin', {
      entity: 'ScheduledValidation',
      name: modelKey,
      accuracy: result.accuracy + '%',
    });

    this._notify();
    return entry;
  }

  /**
   * Check all enabled schedules and run any that are due.
   * Call this on app load or Admin page mount.
   * Returns array of run entries.
   */
  async checkAndRunDue(dataValidationService) {
    const schedules = this.getSchedules();
    const now = new Date();
    const ran = [];

    for (const [modelKey, schedule] of Object.entries(schedules)) {
      if (!schedule.enabled) continue;
      if (!schedule.nextRun) continue;
      if (new Date(schedule.nextRun) > now) continue;

      // Due — run it
      const schedule2 = this.getSchedule(modelKey);
      try {
        let result;
        if (modelKey === 'georisk') {
          if (!schedule2.datasetId) continue;
          result = await dataValidationService.runGeoValidation(schedule2.datasetId);
        } else if (modelKey.startsWith('customerrisk_')) {
          const modelName = modelKey.replace('customerrisk_', '');
          if (!schedule2.datasetId) continue;
          result = await dataValidationService.runCustomerValidation(schedule2.datasetId, modelName);
        } else {
          continue;
        }
        const entry = await this._recordRun(modelKey, result, 'scheduled');
        ran.push(entry);
      } catch (err) {
        const entry = await this._recordFailedRun(modelKey, err, 'scheduled');
        ran.push(entry);
      }
      await this._updateLastRun(modelKey);
    }

    if (ran.length > 0) this._notify();
    return ran;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  async _updateLastRun(modelKey) {
    const all = this.getSchedules();
    if (!all[modelKey]) return;
    all[modelKey].lastRun = new Date().toISOString();
    if (all[modelKey].enabled) {
      all[modelKey].nextRun = this._computeNextRun(
        all[modelKey].frequency, all[modelKey].lastRun, all[modelKey].weekday
      );
    }
    await storage.setJSON(this._entityKey(SCHEDULES_KEY), all);
  }

  _computeNextRun(frequency, lastRun, weekday) {
    const base = lastRun ? new Date(lastRun) : new Date();
    if (frequency === 'daily') {
      const next = new Date(base);
      next.setDate(next.getDate() + 1);
      return next.toISOString();
    }
    // weekly — advance to the specified weekday (0=Sun, 1=Mon, …, 6=Sat)
    const next = new Date(base);
    if (weekday !== undefined && weekday !== null) {
      const targetDay = typeof weekday === 'number' ? weekday : 1;
      const daysUntil = ((targetDay - base.getDay() + 7) % 7) || 7;
      next.setDate(next.getDate() + daysUntil);
    } else {
      next.setDate(next.getDate() + 7);
    }
    return next.toISOString();
  }

  /**
   * Remove all history entries older than `days` days.
   * Returns the number of entries removed.
   */
  async clearHistoryOlderThan(days) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const history = this.getHistory();
    const filtered = history.filter(e => new Date(e.timestamp) >= cutoff);
    const removed = history.length - filtered.length;
    if (removed > 0) {
      await storage.setJSON(this._entityKey(HISTORY_KEY), filtered);
      auditService.log('DELETE', 'Admin', {
        entity: 'ValidationHistory',
        name: `Cleared entries older than ${days} days`,
        count: removed,
      });
      this._notify();
    }
    return removed;
  }

  // ── Subscriptions ────────────────────────────────────────────────────────

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  _notify() {
    this.listeners.forEach(l => l());
  }
}

const scheduledValidationService = new ScheduledValidationService();
export default scheduledValidationService;
